import { useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import {
  ChartLine,
  Droplet,
  Flame,
  History,
  Lightbulb,
  Minus,
  Plus,
  Sparkles,
  TriangleAlert,
  WandSparkles,
  Waves
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { CircuitId, HeaterId, HeaterState, PoolHeatMode } from "@home-control/shared";
import { fetchPoolHistory, NotFoundError, type HistoryRange, type PoolHistory } from "../../api";
import { useHome } from "../state";
import { SectionHeader } from "../components/SectionHeader";
import { Segmented } from "../components/Segmented";
import { Spinner } from "../components/Spinner";
import { ToggleTile } from "../components/ToggleTile";
import { clamp, findRuns, formatHour, formatMonthDay, formatTooltipDate, useContainerWidth } from "./chart";
import "./pool.css";

const circuitOrder: CircuitId[] = ["pool", "spa", "jets", "cleaner", "poolLight"];
const heaterOrder: HeaterId[] = ["pool", "spa"];

const circuitIcons: Record<string, LucideIcon> = {
  poolLight: Lightbulb,
  jets: Sparkles,
  cleaner: WandSparkles,
  spa: Waves
};

const heatModeOptions: { value: PoolHeatMode; label: string }[] = [
  { value: "off", label: "Off" },
  { value: "heater", label: "Heat" },
  { value: "solar", label: "Solar" },
  { value: "solarPreferred", label: "Solar+" }
];

const heatModeLabels: Record<string, string> = {
  off: "Off",
  heater: "Heat",
  solar: "Solar",
  solarPreferred: "Solar+"
};

const setPointMin = 40;
const setPointMax = 104;

export function PoolPage() {
  const { state } = useHome();

  if (!state) {
    return (
      <main className="page pool-page">
        <SectionHeader title="Pool" />
        <p className="placeholder-note">Syncing latest climate data.</p>
      </main>
    );
  }

  return <PoolDetail />;
}

function PoolDetail() {
  const { state, command, isPending, canControlDevices } = useHome();
  const pool = state!.pool;
  const gated = !canControlDevices;

  const circuits = circuitOrder.map((id) => pool.circuits[id]).filter(Boolean);
  const activeLabels = circuits.filter((circuit) => circuit.on).map((circuit) => circuit.label);
  const subtitle = activeLabels.length ? activeLabels.slice(0, 3).join(" · ") : "All circuits off";

  return (
    <main className="page pool-page">
      <SectionHeader title="Pool" />

      <section className="pl-controls">
        <div className="pl-controls-head">
          <span className="pl-controls-chip">
            <Waves size={22} strokeWidth={2.1} />
          </span>
          <div className="pl-controls-names">
            <h2 className="pl-controls-title">Pool</h2>
            <p className="pl-controls-sub">{subtitle}</p>
          </div>
        </div>

        <div className="pl-circuits">
          {circuits.map((circuit) => (
            <ToggleTile
              key={circuit.id}
              icon={circuitIcons[circuit.id] ?? Droplet}
              label={circuit.label}
              on={circuit.on}
              pending={isPending(`pool-circuit-${circuit.id}`)}
              disabled={gated}
              onToggle={() =>
                void command(`pool-circuit-${circuit.id}`, {
                  kind: "setCircuitState",
                  circuit: circuit.id,
                  on: !circuit.on
                })
              }
            />
          ))}
        </div>

        <div className="pl-heaters">
          {heaterOrder
            .map((id) => pool.heaters[id])
            .filter(Boolean)
            .map((heater) => (
              <HeaterCard key={heater.id} heater={heater} gated={gated} />
            ))}
        </div>
      </section>

      <PoolHistoryPanel />
    </main>
  );
}

function HeaterCard({ heater, gated }: { heater: HeaterState; gated: boolean }) {
  const { command, isPending } = useHome();
  const setpointPending = isPending(`pool-heater-${heater.id}-setpoint`);
  const modePending = isPending(`pool-heater-${heater.id}-mode`);
  const flameTint = heater.heatMode !== "off" ? "var(--warm)" : "var(--muted)";
  const shownSetPoint = heater.setPointF ?? 80;
  const modeLabel = heatModeLabels[heater.heatMode] ?? "Ready";

  function nudge(delta: number) {
    const next = clamp(shownSetPoint + delta, setPointMin, setPointMax);
    if (next === shownSetPoint) return;
    void command(`pool-heater-${heater.id}-setpoint`, {
      kind: "setPoolSetPoint",
      heater: heater.id,
      setPointF: next
    });
  }

  return (
    <div className={`pl-heater${gated ? " gated" : ""}`}>
      <div className="pl-heater-head">
        <span
          className="pl-heater-chip"
          style={{ color: flameTint, background: `color-mix(in srgb, ${flameTint} 13%, transparent)` }}
        >
          <Flame size={21} strokeWidth={2.6} />
        </span>
        <div className="pl-heater-names">
          <h3 className="pl-heater-title">{heater.label}</h3>
          <p className="pl-heater-sub">
            {heater.currentTempF ?? "--"}° water · {modeLabel}
          </p>
        </div>
      </div>

      <div className="pl-stepper">
        <button
          type="button"
          className="pl-step-button"
          aria-label={`Lower ${heater.label} set point`}
          disabled={gated || setpointPending || shownSetPoint <= setPointMin}
          onClick={() => nudge(-1)}
        >
          <Minus size={18} strokeWidth={2.8} />
        </button>
        <div className="pl-stepper-center">
          {setpointPending ? (
            <span className="pl-stepper-spinner">
              <Spinner size={18} />
            </span>
          ) : null}
          <span className="pl-setpoint">{shownSetPoint}°</span>
          <span className="pl-setpoint-caption">SET</span>
        </div>
        <button
          type="button"
          className="pl-step-button"
          aria-label={`Raise ${heater.label} set point`}
          disabled={gated || setpointPending || shownSetPoint >= setPointMax}
          onClick={() => nudge(1)}
        >
          <Plus size={18} strokeWidth={2.8} />
        </button>
      </div>

      <div className="pl-heater-modes">
        <Segmented
          options={heatModeOptions}
          value={heater.heatMode}
          tint="var(--pool)"
          disabled={gated || modePending}
          onChange={(heatMode) =>
            void command(`pool-heater-${heater.id}-mode`, {
              kind: "setPoolHeatMode",
              heater: heater.id,
              heatMode
            })
          }
        />
        {modePending ? (
          <span className="pl-mode-spinner">
            <Spinner size={16} />
          </span>
        ) : null}
      </div>
    </div>
  );
}

/* Pool history (spec §4.2) --------------------------------------------------- */

type PoolRange = "1D" | "7D";

interface HistoryEntry {
  status: "loading" | "ready" | "error";
  data?: PoolHistory;
  message?: string;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function toDateInputValue(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function PoolHistoryPanel() {
  const { state, token } = useHome();
  const pool = state!.pool;

  const [range, setRange] = useState<PoolRange>("1D");
  const [selectedDay, setSelectedDay] = useState<Date>(() => startOfDay(new Date()));
  const [entries, setEntries] = useState<Record<string, HistoryEntry>>({});
  const loadingKeys = useRef(new Set<string>());

  const window = useMemo(() => {
    const from = range === "1D" ? selectedDay : addDays(selectedDay, -6);
    const to = addDays(selectedDay, 1);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [range, selectedDay]);

  const apiRange: HistoryRange = range === "1D" ? "24h" : "7d";
  const key = `${apiRange}-${window.from}-${window.to}`;
  const entry = entries[key];

  useEffect(() => {
    if (loadingKeys.current.has(key)) return;
    loadingKeys.current.add(key);
    setEntries((current) => ({ ...current, [key]: { status: "loading" } }));
    fetchPoolHistory(token, apiRange, window.from, window.to)
      .then((data) => {
        setEntries((current) => ({ ...current, [key]: { status: "ready", data } }));
      })
      .catch((error: unknown) => {
        const message =
          error instanceof NotFoundError
            ? "Pool history endpoint is not available yet."
            : error instanceof Error && error.message
              ? error.message
              : "Pool history read failed.";
        setEntries((current) => ({ ...current, [key]: { status: "error", message } }));
      })
      .finally(() => {
        loadingKeys.current.delete(key);
      });
  }, [token, key, apiRange, window.from, window.to]);

  const pumpOn = pool.circuits.pool.on || pool.circuits.spa.on;
  const heaters = heaterOrder.map((id) => pool.heaters[id]).filter(Boolean);
  const anyHeating = heaters.some((heater) => heater.heating === true);
  const primaryHeater = pool.heaters.pool;
  const heaterChipLabel = anyHeating
    ? "Heating"
    : primaryHeater
      ? primaryHeater.heatMode === "off"
        ? "Heat off"
        : heatModeLabels[primaryHeater.heatMode] ?? "Heat ready"
      : "Heat ready";
  const waterTemp = primaryHeater?.currentTempF ?? null;

  const chartHeight = range === "1D" ? 214 : 226;

  return (
    <section className="pl-history">
      <div className="pl-history-head">
        <div className="pl-history-headings">
          <h2 className="pl-history-title">Pool history</h2>
          <div className="pl-chips">
            <span className="pl-chip" style={{ color: "var(--pool)" }}>
              <Droplet size={12} strokeWidth={2.8} />
              {pumpOn ? "Pump on" : "Pump idle"}
            </span>
            <span className="pl-chip warm" style={{ color: "var(--warm)" }}>
              <Flame size={12} strokeWidth={2.8} />
              {heaterChipLabel}
            </span>
          </div>
        </div>
        <div className="pl-water">
          <span className="pl-water-value">{waterTemp ?? "--"}°</span>
          <span className="pl-water-caption">WATER</span>
        </div>
      </div>

      <div className="pl-range-row">
        <div className="range-picker" role="group" aria-label="Pool history range">
          {(["1D", "7D"] as const).map((option) => (
            <button
              key={option}
              type="button"
              className={`range-option${range === option ? " selected" : ""}`}
              aria-pressed={range === option}
              onClick={() => setRange(option)}
            >
              {option}
            </button>
          ))}
        </div>
        <input
          type="date"
          className="pl-date"
          aria-label="History date"
          value={toDateInputValue(selectedDay)}
          max={toDateInputValue(startOfDay(new Date()))}
          onChange={(event) => {
            const parts = event.target.value.split("-").map(Number);
            if (parts.length === 3 && parts.every((part) => Number.isFinite(part))) {
              setSelectedDay(new Date(parts[0], parts[1] - 1, parts[2]));
            }
          }}
        />
      </div>

      <PoolHistoryBody entry={entry} chartHeight={chartHeight} chartKey={key} />
    </section>
  );
}

function PoolHistoryBody({
  entry,
  chartHeight,
  chartKey
}: {
  entry: HistoryEntry | undefined;
  chartHeight: number;
  chartKey: string;
}) {
  const allPoints = useMemo<ParsedPoint[]>(() => {
    if (!entry?.data) return [];
    return entry.data.points
      .map((point) => ({
        time: new Date(point.timestamp),
        temp: point.temperatureF,
        pump: point.pumpOn ?? 0,
        heater: point.heaterOn ?? 0
      }))
      .filter((point) => !Number.isNaN(point.time.getTime()));
  }, [entry?.data]);

  const drawable = useMemo(
    () =>
      allPoints
        .map((point, index) => ({ ...point, index }))
        .filter((point): point is DrawablePoint => point.temp !== null),
    [allPoints]
  );

  if (!entry || entry.status === "loading") {
    return (
      <div className="pl-chart-message" style={{ height: chartHeight }}>
        <History size={22} strokeWidth={2.4} />
        <span>Loading pool history</span>
      </div>
    );
  }
  if (entry.status === "error") {
    return (
      <div className="pl-chart-message" style={{ height: chartHeight }}>
        <TriangleAlert size={22} strokeWidth={2.4} />
        <span>{entry.message}</span>
      </div>
    );
  }
  if (drawable.length === 0) {
    return (
      <div className="pl-chart-message" style={{ height: chartHeight }}>
        <ChartLine size={22} strokeWidth={2.4} />
        <span>No pool history yet</span>
      </div>
    );
  }

  return (
    <>
      <PoolChart key={chartKey} height={chartHeight} totalCount={allPoints.length} points={drawable} />
      <div className="chart-legend">
        <span className="chart-legend-item">
          <span className="chart-legend-dot" style={{ background: "var(--pool)" }} />
          Water temp
        </span>
        <span className="chart-legend-item">
          <span className="chart-legend-dot" style={{ background: "color-mix(in srgb, var(--pool) 52%, transparent)" }} />
          Pump
        </span>
        <span className="chart-legend-item">
          <span className="chart-legend-dot" style={{ background: "color-mix(in srgb, var(--warm) 72%, transparent)" }} />
          Heater
        </span>
      </div>
    </>
  );
}

interface ParsedPoint {
  time: Date;
  temp: number | null;
  pump: number;
  heater: number;
}

interface DrawablePoint {
  time: Date;
  temp: number;
  pump: number;
  heater: number;
  /** Index within ALL parsed points so gaps keep their spacing (spec §4.2). */
  index: number;
}

const poolInset = { top: 10, left: 30, bottom: 28, right: 8 };

function PoolChart({
  height,
  totalCount,
  points
}: {
  height: number;
  totalCount: number;
  points: DrawablePoint[];
}) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const [selected, setSelected] = useState<number | null>(null);

  const plotWidth = Math.max(1, width - poolInset.left - poolInset.right);
  const plotHeight = height - poolInset.top - poolInset.bottom;
  const plotBottom = poolInset.top + plotHeight;

  const temps = points.map((point) => point.temp);
  const lower = temps.length ? Math.floor(Math.min(...temps) - 2) : 74;
  const upper = temps.length ? Math.max(Math.ceil(Math.max(...temps) + 2), lower + 1) : 90;

  const x = (point: DrawablePoint) =>
    poolInset.left + (totalCount <= 1 ? plotWidth / 2 : (point.index / (totalCount - 1)) * plotWidth);
  const y = (value: number) => plotBottom - ((value - lower) / (upper - lower)) * plotHeight;

  const tempPath = points
    .map((point, order) => `${order === 0 ? "M" : "L"} ${x(point).toFixed(2)} ${y(point.temp).toFixed(2)}`)
    .join(" ");

  function areaPaths(active: (point: DrawablePoint) => boolean): string[] {
    return findRuns(points.length, (index) => active(points[index])).map(([start, end]) => {
      const first = points[start];
      const last = points[end];
      // Run edges extend to the midpoint toward the neighboring drawable point.
      const x0 = start > 0 ? (x(points[start - 1]) + x(first)) / 2 : x(first);
      const x1 = end < points.length - 1 ? (x(points[end + 1]) + x(last)) / 2 : x(last);
      let path = `M ${x0.toFixed(2)} ${plotBottom.toFixed(2)} L ${x0.toFixed(2)} ${y(first.temp).toFixed(2)} `;
      for (let i = start; i <= end; i += 1) {
        path += `L ${x(points[i]).toFixed(2)} ${y(points[i].temp).toFixed(2)} `;
      }
      path += `L ${x1.toFixed(2)} ${y(last.temp).toFixed(2)} L ${x1.toFixed(2)} ${plotBottom.toFixed(2)} Z`;
      return path;
    });
  }

  const pumpAreas = areaPaths((point) => point.pump > 0.05);
  const heaterAreas = areaPaths((point) => point.heater > 0.05);

  const sameDay = points.every(
    (point) =>
      point.time.getFullYear() === points[0].time.getFullYear() &&
      point.time.getMonth() === points[0].time.getMonth() &&
      point.time.getDate() === points[0].time.getDate()
  );
  const formatAxis = (date: Date) => (sameDay ? formatHour(date) : formatMonthDay(date));
  const axisIndexes =
    points.length === 1 ? [0] : [0, Math.floor((points.length - 1) / 2), points.length - 1];

  function scrub(event: React.PointerEvent<HTMLDivElement>) {
    const el = ref.current;
    if (!el || points.length === 0) return;
    const rect = el.getBoundingClientRect();
    const pointerX = event.clientX - rect.left;
    let nearest = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    points.forEach((point, index) => {
      const distance = Math.abs(x(point) - pointerX);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = index;
      }
    });
    setSelected(nearest);
  }

  const selectedPoint = selected === null ? null : points[selected];
  const tooltipX =
    selectedPoint === null ? 0 : clamp(x(selectedPoint), 84, Math.max(84, width - 84));

  return (
    <div
      ref={ref}
      className="chart-surface"
      style={{ height }}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        scrub(event);
      }}
      onPointerMove={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) scrub(event);
      }}
      onPointerUp={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      }}
    >
      {width > 0 ? (
        <svg width={width} height={height} aria-hidden="true">
          {[0, 1, 2, 3].map((step) => {
            const value = lower + ((upper - lower) * step) / 3;
            const lineY = y(value);
            return (
              <g key={step}>
                <line
                  x1={poolInset.left}
                  x2={width - poolInset.right}
                  y1={lineY}
                  y2={lineY}
                  stroke="var(--stroke)"
                  strokeWidth={1}
                  opacity={step === 0 ? 1 : 0.72}
                />
                <text className="chart-axis-label" x={poolInset.left - 6} y={lineY + 3} textAnchor="end">
                  {Math.round(value)}°
                </text>
              </g>
            );
          })}

          {pumpAreas.map((path, index) => (
            <path key={`pump-${index}`} d={path} fill="color-mix(in srgb, var(--pool) 20%, transparent)" />
          ))}
          {heaterAreas.map((path, index) => (
            <path key={`heater-${index}`} d={path} fill="color-mix(in srgb, var(--warm) 26%, transparent)" />
          ))}

          <path
            d={tempPath}
            fill="none"
            stroke="var(--pool)"
            strokeWidth={2.25}
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {selected === null ? (
            <circle
              cx={x(points[points.length - 1])}
              cy={y(points[points.length - 1].temp)}
              r={4}
              fill="var(--surface)"
              stroke="var(--pool)"
              strokeWidth={2}
            />
          ) : null}

          {selectedPoint ? (
            <g>
              <line
                x1={x(selectedPoint)}
                x2={x(selectedPoint)}
                y1={poolInset.top}
                y2={plotBottom}
                stroke="color-mix(in srgb, var(--ink) 28%, transparent)"
                strokeWidth={1}
                strokeDasharray="4 4"
              />
              <circle
                cx={x(selectedPoint)}
                cy={y(selectedPoint.temp)}
                r={4.5}
                fill="var(--surface)"
                stroke="var(--pool)"
                strokeWidth={2}
              />
            </g>
          ) : null}

          {axisIndexes.map((index, order) => (
            <text
              key={`axis-${index}`}
              className="chart-axis-label"
              x={x(points[index])}
              y={plotBottom + 18}
              textAnchor={
                order === 0 ? "start" : order === axisIndexes.length - 1 && axisIndexes.length > 1 ? "end" : "middle"
              }
            >
              {formatAxis(points[index].time)}
            </text>
          ))}
        </svg>
      ) : null}

      {selectedPoint ? (
        <div className="chart-tooltip" style={{ left: tooltipX, top: 23 }}>
          <span className="chart-tooltip-line">{formatTooltipDate(selectedPoint.time)}</span>
          <span className="chart-tooltip-line">
            <span className="chart-tooltip-dot" style={{ background: "var(--pool)" }} />
            {Math.round(selectedPoint.temp)}°
            <span className="chart-tooltip-status">{poolPointStatus(selectedPoint)}</span>
          </span>
        </div>
      ) : null}
    </div>
  );
}

function poolPointStatus(point: DrawablePoint): string {
  const pump = point.pump > 0.05;
  const heater = point.heater > 0.05;
  if (pump && heater) return "Pump + heat";
  if (pump) return "Pump on";
  if (heater) return "Heater on";
  return "Idle";
}
